if (!customElements.get('sectionly-collections-product-tabs')) {
  class SectionlyCollectionsProductTabs extends HTMLElement {
    constructor() {
      super();
      
      this.selectors = {
        tabButtons: '.sectionly-tab-button',
        tabPanels: '.sectionly-tab-panel',
        viewDetailsButtons: '.sectionly-view-details-btn-bottom',
        productLinks: '.sectionly-product-link',
        swiperContainers: '.sectionly-products-swiper'
      };
      
      this.swipers = [];
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.domNodes = this.queryDomNodes(this.selectors);
      this.currentActiveIndex = 0;
      this.setupEventListeners();
      this.setupIntersectionObserver();
      this.initSwipers();
    }

    queryDomNodes(selectors) {
      const nodes = {};
      Object.keys(selectors).forEach(key => {
        nodes[key] = this.querySelectorAll(selectors[key]);
      });
      return nodes;
    }

    setupEventListeners() {
      // Tab button click events
      this.domNodes.tabButtons.forEach((button, index) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.switchTab(index);
        });
      });

      // View details button click events
      this.domNodes.viewDetailsButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Find the parent product link and trigger navigation
          const productLink = button.closest('.sectionly-product-card').querySelector('.sectionly-product-link');
          if (productLink && productLink.href) {
            window.location.href = productLink.href;
          }
        });
      });

      // Product card hover events for better mobile experience
      this.querySelectorAll('.sectionly-product-card').forEach(card => {
        // Remove any existing hover classes first
        card.classList.remove('sectionly-touch-hover');
        
        // Desktop hover events
        card.addEventListener('mouseenter', (e) => {
          this.clearAllHoverStates();
          card.classList.add('sectionly-hover-active');
        });

        card.addEventListener('mouseleave', (e) => {
          card.classList.remove('sectionly-hover-active');
        });
        
        // Mobile touch events
        card.addEventListener('touchstart', (e) => {
          this.clearAllHoverStates();
          card.classList.add('sectionly-touch-hover');
        });

        card.addEventListener('touchend', (e) => {
          setTimeout(() => {
            card.classList.remove('sectionly-touch-hover');
          }, 300);
        });

        // Also handle touch cancel events
        card.addEventListener('touchcancel', (e) => {
          card.classList.remove('sectionly-touch-hover');
        });
      });

      // Keyboard navigation for accessibility
      this.domNodes.tabButtons.forEach((button, index) => {
        button.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.switchTab(index);
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            const prevIndex = index > 0 ? index - 1 : this.domNodes.tabButtons.length - 1;
            this.switchTab(prevIndex);
            this.domNodes.tabButtons[prevIndex].focus();
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            const nextIndex = index < this.domNodes.tabButtons.length - 1 ? index + 1 : 0;
            this.switchTab(nextIndex);
            this.domNodes.tabButtons[nextIndex].focus();
          }
        });
      });
    }

    // Helper method to clear all hover states
    clearAllHoverStates() {
      this.querySelectorAll('.sectionly-product-card').forEach(card => {
        card.classList.remove('sectionly-hover-active', 'sectionly-touch-hover');
      });
    }

    switchTab(index) {
      if (index === this.currentActiveIndex) return;

      // Clear all hover states when switching tabs
      this.clearAllHoverStates();

      // Remove active class from current tab and panel
      this.domNodes.tabButtons[this.currentActiveIndex]?.classList.remove('sectionly-active');
      this.domNodes.tabPanels[this.currentActiveIndex]?.classList.remove('sectionly-active');

      // Add active class to new tab and panel
      this.domNodes.tabButtons[index]?.classList.add('sectionly-active');
      this.domNodes.tabPanels[index]?.classList.add('sectionly-active');

      // Update current active index
      this.currentActiveIndex = index;

      // Update ARIA attributes for accessibility
      this.updateAriaAttributes(index);

      // Update Swiper in the newly active panel
      setTimeout(() => {
        if (this.swipers[index]) {
          this.swipers[index].update();
        }
      }, 100);

      // Trigger custom event for analytics or other integrations
      this.dispatchEvent(new CustomEvent('sectionly:tab-changed', {
        detail: {
          activeIndex: index,
          activeTabId: this.domNodes.tabButtons[index]?.dataset.tabId
        },
        bubbles: true
      }));

      // Lazy load images in the newly active panel if needed
      this.lazyLoadImages(this.domNodes.tabPanels[index]);
    }

    updateAriaAttributes(activeIndex) {
      this.domNodes.tabButtons.forEach((button, index) => {
        button.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
        button.setAttribute('tabindex', index === activeIndex ? '0' : '-1');
      });

      this.domNodes.tabPanels.forEach((panel, index) => {
        panel.setAttribute('aria-hidden', index === activeIndex ? 'false' : 'true');
      });
    }

    lazyLoadImages(panel) {
      if (!panel) return;

      const images = panel.querySelectorAll('img[data-src]');
      images.forEach(img => {
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
      });
    }

    setupIntersectionObserver() {
      // Animate elements when they come into view
      const animatedElements = this.querySelectorAll('.s-scroll-trigger');
      
      if (animatedElements.length > 0 && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate--fade-in');
              observer.unobserve(entry.target);
            }
          });
        }, {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px'
        });

        animatedElements.forEach(element => {
          observer.observe(element);
        });
      }
    }

    // Public method to programmatically switch tabs
    setActiveTab(index) {
      if (index >= 0 && index < this.domNodes.tabButtons.length) {
        this.switchTab(index);
      }
    }

    // Public method to get current active tab
    getActiveTab() {
      return {
        index: this.currentActiveIndex,
        tabId: this.domNodes.tabButtons[this.currentActiveIndex]?.dataset.tabId,
        button: this.domNodes.tabButtons[this.currentActiveIndex],
        panel: this.domNodes.tabPanels[this.currentActiveIndex]
      };
    }

    disconnectedCallback() {
      // Clean up event listeners if needed
      this.domNodes.tabButtons.forEach(button => {
        button.removeEventListener('click', this.switchTab);
        button.removeEventListener('keydown', this.switchTab);
      });
    }

    initSwipers() {
      // Wait for Swiper library to load
      if (typeof Swiper === 'undefined') {
        setTimeout(() => this.initSwipers(), 100);
        return;
      }

      this.domNodes.swiperContainers.forEach((container, index) => {
        const showNavigation = container.closest('section').querySelector('[data-section-id]')?.dataset.showNavigationArrows !== 'false';
        
        const swiperConfig = {
          slidesPerView: 1,
          spaceBetween: 20,
          loop: true,
          breakpoints: {
            480: {
              slidesPerView: 2,
              spaceBetween: 15,
            },
            768: {
              slidesPerView: 3,
              spaceBetween: 20,
            },
            992: {
              slidesPerView: 4,
              spaceBetween: 25,
            },
            1200: {
              slidesPerView: 5,
              spaceBetween: 30,
            }
          },
          on: {
            init: function() {
              // Trigger resize to ensure proper layout
              setTimeout(() => {
                this.update();
              }, 100);
            }
          }
        };

        // Add navigation only if arrows are present
        const nextButton = container.querySelector('.sectionly-swiper-button-next');
        const prevButton = container.querySelector('.sectionly-swiper-button-prev');
        
        if (nextButton && prevButton) {
          swiperConfig.navigation = {
            nextEl: nextButton,
            prevEl: prevButton,
          };
        }

        const swiper = new Swiper(container, swiperConfig);
        this.swipers.push(swiper);
      });
    }
  }

  // Register the custom element
  customElements.define('sectionly-collections-product-tabs', SectionlyCollectionsProductTabs);
}

// Additional utility functions for enhanced functionality
document.addEventListener('DOMContentLoaded', function() {
  // Handle responsive behavior
  function handleResponsiveChanges() {
    const collectionsComponents = document.querySelectorAll('sectionly-collections-product-tabs');
    
    collectionsComponents.forEach(component => {
      // Update all Swiper instances
      if (component.swipers) {
        component.swipers.forEach(swiper => {
          if (swiper && swiper.update) {
            swiper.update();
          }
        });
      }
    });
  }

  // Handle window resize
  let resizeTimeout;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(handleResponsiveChanges, 250);
  });

  // Initial setup
  handleResponsiveChanges();

  // Handle touch devices for better hover effects
  if ('ontouchstart' in window) {
    document.body.classList.add('sectionly-touch-device');
    
    // Add CSS for touch devices - Updated to match new CSS structure
    const style = document.createElement('style');
    style.textContent = `
      .sectionly-collections-product-tabs .sectionly-product-bottom-expansion {
        display: none !important;
      }
      .sectionly-collections-product-tabs .sectionly-product-card:not(.sectionly-hover-active):not(.sectionly-touch-hover):hover {
        transform: none !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
        z-index: auto !important;
      }
      .sectionly-touch-device .sectionly-collections-product-tabs .sectionly-product-card.sectionly-touch-hover .sectionly-product-bottom-expansion,
      .sectionly-touch-device .sectionly-collections-product-tabs .sectionly-product-card.sectionly-hover-active .sectionly-product-bottom-expansion,
      .sectionly-collections-product-tabs .sectionly-product-card.sectionly-hover-active .sectionly-product-bottom-expansion,
      .sectionly-collections-product-tabs .sectionly-product-card.sectionly-touch-hover .sectionly-product-bottom-expansion {
        display: flex !important;
        opacity: 1 !important;
        visibility: visible !important;
        height: 60px !important;
        padding: 0 24px !important;
        background: #E7E3DF !important;
        transform: translateY(0);
        align-items: center;
        justify-content: center;
      }
      .sectionly-touch-device .sectionly-collections-product-tabs .sectionly-product-card.sectionly-touch-hover,
      .sectionly-touch-device .sectionly-collections-product-tabs .sectionly-product-card.sectionly-hover-active,
      .sectionly-collections-product-tabs .sectionly-product-card.sectionly-hover-active {
        transform: scale(1.05) !important;
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
        z-index: 2 !important;
      }
      .sectionly-touch-device .sectionly-collections-product-tabs .sectionly-product-card.sectionly-touch-hover .sectionly-image,
      .sectionly-touch-device .sectionly-collections-product-tabs .sectionly-product-card.sectionly-hover-active .sectionly-image,
      .sectionly-collections-product-tabs .sectionly-product-card.sectionly-hover-active .sectionly-image {
        transform: scale(1.1);
      }
    `;
    document.head.appendChild(style);
  }

  // Performance optimization: Debounce scroll events
  let scrollTimeout;
  window.addEventListener('scroll', function() {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      // Trigger any scroll-based animations or lazy loading
      const event = new CustomEvent('sectionly:scroll-optimized');
      document.dispatchEvent(event);
    }, 16); // ~60fps
  });
}); 